import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCustomerList } from "gen/endpoints/customer/customer";
import { useIntegrationTestingReportResultsCreate } from "gen/endpoints/integration-testing/integration-testing";
import { getInternalIntegrationTestingQueueTableQueryKey } from "gen/endpoints/integration-testing-queue/integration-testing-queue";
import { FundFamilyTestingResultEnum } from "gen/model/fundFamilyTestingResultEnum";
import { IntegrationTestingShadowResultDataRequest } from "gen/model/integrationTestingShadowResultDataRequest";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";

export interface InternalIntegrationTestingValidationChecksModalProps {
  onCloseComplete(): void;
}

interface FormData {
  customer_names: string[];
}

export const InternalIntegrationTestingValidationChecksModal: React.FC<
  InternalIntegrationTestingValidationChecksModalProps
> = ({ onCloseComplete }) => {
  const { t } = useTranslation(["internal", "auth", "common"]);
  const queryClient = useQueryClient();
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const form = useForm<FormData>();

  const { data: customerListData } = useCustomerList();

  const { mutateAsync: createRunValidationChecks } =
    useIntegrationTestingReportResultsCreate();

  const customerSelectOptions = useMemo(
    () =>
      customerListData?.map((customer) => (
        <SelectOption
          key={`customer_name_${customer.name}`}
          value={customer.name}
        >
          {customer.name}
        </SelectOption>
      )),
    [customerListData],
  );
  const onSubmit = async (data: FormData) => {
    const createData: IntegrationTestingShadowResultDataRequest = {
      fund_family_testing_report_results: data.customer_names.map(
        (customerName) => ({
          customer_name: customerName,
          result: FundFamilyTestingResultEnum.success,
          total_time: 0,
        }),
      ),
    };
    await createRunValidationChecks({ data: createData });
    await queryClient.invalidateQueries({
      queryKey: getInternalIntegrationTestingQueueTableQueryKey(),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={onClose}
          successMessage={t("internal:modal.runValidationChecks.success")}
        >
          <ModalHeader>
            {t("internal:modal.runValidationChecks.title")}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <FormSelect
              isMulti={true}
              title={t("common:customer")}
              name="customer_names"
            >
              {customerSelectOptions}
            </FormSelect>
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} autoFocus={true} />}
              confirm={<FormSubmitButton>{t("common:save")}</FormSubmitButton>}
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
